import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface SupportCategory {
  value: string;
  label: string;
}

export interface SupportTicket {
  id: number;
  case_id: string;
  subject: string;
  category: string;
  category_label: string;
  description: string;
  status: string;
  priority: string;
  assigned_to_name: string | null;
  tags: string[];
  patient_name: string;
  created_at: string;
  updated_at: string;
  remarks: SupportRemark[];
}

export interface SupportRemark {
  id: number;
  remark: string;
  remark_type: string;
  created_at: string;
}

export interface CreateTicketPayload {
  subject: string;
  category: string;
  description?: string;
  priority?: string;
}

export interface CreateTicketResponse {
  id: number;
  case_id: string;
  status: string;
  category: string;
  subject: string;
  created_at: string;
  message: string;
}

export interface TicketListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SupportTicket[];
}

export interface CustomerSupportRecord {
  kind: 'payment' | 'booking';
  reference: string;
  title: string;
  service?: string;
  date: string | null;
  status: string;
  status_source?: string;
  status_label: string;
  payment_status?: string;
  payment_status_label?: string;
  amount?: string;
  currency?: string;
  invoice_available?: boolean;
  actions?: string[];
  detail_url: string;
}

export interface CustomerSupportReply {
  answer: string;
  intent: string;
  destination: 'payments' | 'bookings' | 'services' | 'teleconsultation' | 'profile' | 'ai_family_doctor' | 'support' | null;
  records: CustomerSupportRecord[];
  ticket_category?: string;
  ticket_subject?: string;
}

const supportService = {
  async chat(message: string, language: string): Promise<CustomerSupportReply> {
    const response = await axiosInstance.post(API_ENDPOINTS.customerSupport.chat, {
      message,
      language,
    });
    return response.data;
  },

  async getCategories(): Promise<SupportCategory[]> {
    const response = await axiosInstance.get(API_ENDPOINTS.support.categories);
    return response.data.categories || response.data || [];
  },

  async getTickets(params?: { status?: string; page?: number }): Promise<TicketListResponse> {
    const response = await axiosInstance.get(API_ENDPOINTS.support.tickets, { params });
    // Handle both paginated and non-paginated responses
    if (response.data.results) {
      return response.data;
    }
    const results = Array.isArray(response.data) ? response.data : [];
    return { count: results.length, next: null, previous: null, results };
  },

  async getTicket(id: number): Promise<SupportTicket> {
    const response = await axiosInstance.get(API_ENDPOINTS.support.ticketDetail(id));
    return response.data;
  },

  async createTicket(data: CreateTicketPayload): Promise<CreateTicketResponse> {
    const response = await axiosInstance.post(API_ENDPOINTS.support.createTicket, data);
    return response.data;
  },

  async addComment(id: number, comment: string): Promise<void> {
    await axiosInstance.post(API_ENDPOINTS.support.addComment(id), { comment });
  },
};

export default supportService;
